import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { buildAppointmentConflictPayload } from "./buildAppointmentConflictPayload";
import {
  checkAppointmentConflicts,
  type CheckAppointmentConflictsResponse,
} from "./checkAppointmentConflicts";

const DEBOUNCE_MS = 400;

type AssigneeOption = { value: number | string; label: string };

const normalizeAssignees = (
  assignee: AssigneeOption[] | null | undefined
): AssigneeOption[] => (Array.isArray(assignee) ? assignee : []);

type Params = {
  assignee: AssigneeOption[] | null | undefined;
  date: string;
  startTimeLocal: string;
  endTimeLocal: string;
  duration: string;
  timezone: string;
  excludeAppointmentId?: number | string | null;
  extraDates?: string[];
};

const mergeConflictResponses = (
  responses: CheckAppointmentConflictsResponse[]
): CheckAppointmentConflictsResponse => {
  const byAssignee = new Map<
    number,
    CheckAppointmentConflictsResponse["conflicts"][0]
  >();

  for (const res of responses) {
    for (const group of res.conflicts) {
      const existing = byAssignee.get(group.assigneeId);
      if (!existing) {
        byAssignee.set(group.assigneeId, {
          ...group,
          appointments: [...group.appointments],
        });
        continue;
      }
      const seen = new Set(existing.appointments.map((a) => a.id));
      for (const appt of group.appointments) {
        if (!seen.has(appt.id)) {
          existing.appointments.push(appt);
          seen.add(appt.id);
        }
      }
    }
  }

  const conflicts = Array.from(byAssignee.values());
  return { conflicts, hasConflicts: conflicts.length > 0 };
};

export function useAppointmentConflicts(params: Params) {
  const safeParams = {
    ...params,
    assignee: normalizeAssignees(params.assignee),
  };
  const [debouncedParams, setDebouncedParams] = useState(safeParams);

  const assigneeKey = safeParams.assignee.map((a) => a.value).join(",");
  const extraDatesKey = (safeParams.extraDates ?? []).join(",");

  const paramsPendingDebounce =
    assigneeKey !== normalizeAssignees(debouncedParams.assignee).map((a) => a.value).join(",") ||
    params.date !== debouncedParams.date ||
    params.startTimeLocal !== debouncedParams.startTimeLocal ||
    params.endTimeLocal !== debouncedParams.endTimeLocal ||
    params.duration !== debouncedParams.duration ||
    params.timezone !== debouncedParams.timezone ||
    String(params.excludeAppointmentId ?? "") !==
      String(debouncedParams.excludeAppointmentId ?? "") ||
    extraDatesKey !== (debouncedParams.extraDates ?? []).join(",");

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedParams({ ...params, assignee: normalizeAssignees(params.assignee) }),
      DEBOUNCE_MS
    );
    return () => window.clearTimeout(handle);
  }, [
    assigneeKey,
    params.date,
    params.startTimeLocal,
    params.endTimeLocal,
    params.duration,
    params.timezone,
    params.excludeAppointmentId,
    extraDatesKey,
  ]);

  const datesToCheck = useMemo(() => {
    const dates: string[] = [];
    if (debouncedParams.date) {
      dates.push(debouncedParams.date);
    }
    for (const d of debouncedParams.extraDates ?? []) {
      if (d && !dates.includes(d)) dates.push(d);
    }
    return dates;
  }, [debouncedParams.date, debouncedParams.extraDates]);

  const payloads = useMemo(
    () =>
      datesToCheck
        .map((date) =>
          buildAppointmentConflictPayload(debouncedParams, date)
        )
        .filter((p) => p != null),
    [datesToCheck, debouncedParams]
  );

  const queryKey = useMemo(
    () => ["appointment-conflicts", payloads] as const,
    [payloads]
  );

  const query = useQuery(
    queryKey,
    async () => {
      if (payloads.length === 0) {
        return { conflicts: [], hasConflicts: false };
      }
      const results = await Promise.all(
        payloads.map((payload) => checkAppointmentConflicts(payload!))
      );
      return mergeConflictResponses(results);
    },
    {
      enabled: payloads.length > 0,
      staleTime: 30_000,
      keepPreviousData: false,
      retry: 1,
    }
  );

  const resolvedConflicts = query.data?.conflicts ?? [];

  return {
    conflicts: paramsPendingDebounce ? [] : resolvedConflicts,
    hasConflicts: !paramsPendingDebounce && (query.data?.hasConflicts ?? false),
    isChecking: payloads.length > 0 && query.isFetching,
    checkError: query.isError,
  };
}

